import React, { useEffect, useState } from 'react';
import { Arbeitsgruppe } from '../types';
import { authenticatedFetch } from '../libraries/auth';
import { useAppDispatch, registerArbeitsgruppe, unregisterArbeitsgruppe } from '../libraries/AppContext';
import LoadingSpinner from './LoadingSpinner';

const ALREADY_REGISTERED_MESSAGE = 'Sie sind bereits für eine andere Challenge angemeldet. Sollten Sie sich umentschieden haben, bitten wir Sie, sich zunächst bei Ihrer vorherigen Challenge abzumelden, bevor Sie sich erneut anmelden können.';

interface ArbeitsgruppeDetailPageProps {
    arbeitsgruppe: Arbeitsgruppe;
    myArbeitsgruppenIds: number[];
    myParticipantId: number | null;
    onNavigateToArbeitsgruppe?: (id: number) => void;
}

import ImageViewer from './ImageViewer';

const ArbeitsgruppeDetailPage: React.FC<ArbeitsgruppeDetailPageProps> = ({ arbeitsgruppe, myArbeitsgruppenIds, myParticipantId, onNavigateToArbeitsgruppe }) => {
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const [alreadyRegisteredGroupId, setAlreadyRegisteredGroupId] = useState<number | null>(null);

    useEffect(() => {
        // Reset previous error/link state when switching groups
        setError(null);
        setAlreadyRegisteredGroupId(null);
    }, [arbeitsgruppe.id]);

    const sanitizeHTML = (html: string) => {
        if (typeof DOMParser === 'undefined') {
            return html;
        }
        const doc = new DOMParser().parseFromString(html, 'text/html');
        doc.querySelectorAll('script, style').forEach(el => el.remove());
        doc.querySelectorAll('*').forEach(el => {
            for (const attr of Array.from(el.attributes)) {
                if (attr.name.startsWith('on')) {
                    el.removeAttribute(attr.name);
                }
            }
        });
        return doc.body.innerHTML;
    };

    const safeContent = arbeitsgruppe.content ? sanitizeHTML(arbeitsgruppe.content) : '';

    const isRegistered = myArbeitsgruppenIds.includes(arbeitsgruppe.id);
    const isFull = arbeitsgruppe.currentParticipantsCount >= arbeitsgruppe.maxParticipantsCount;

    const handleToggleRegistration = async () => {
        if (myParticipantId === null) {
            setError('Teilnehmer-ID nicht gefunden');
            return;
        }

        setLoading(true);
        setError(null);
        setAlreadyRegisteredGroupId(null);
        try {
            const API_URL = import.meta.env.VITE_API_URL || '';
            const response = await authenticatedFetch(`${API_URL}/api/arbeitsgruppen/${arbeitsgruppe.id}/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({})
            });

            if (!response.ok) {
                const data = await response.json();
                const alreadyRegisteredId = data?.arbeitgruppenId ?? data?.arbeitsgruppenId;
                if (data?.error === 'already_registered_in_another_group' && typeof alreadyRegisteredId === 'number') {
                    setAlreadyRegisteredGroupId(alreadyRegisteredId);
                    setError(ALREADY_REGISTERED_MESSAGE);
                    return;
                }
                throw new Error(data.error || 'Failed to update registration');
            }
            setAlreadyRegisteredGroupId(null);

            // Update reducer state based on registration action
            if (isRegistered) {
                unregisterArbeitsgruppe(dispatch, arbeitsgruppe.id, myParticipantId);
            } else {
                registerArbeitsgruppe(dispatch, arbeitsgruppe.id, myParticipantId);
            }
        } catch (e: any) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    const handleContentClick = (e: React.MouseEvent<HTMLDivElement>) => {
        const target = e.target as HTMLElement;
        if (target.tagName === 'IMG') {
            const img = target as HTMLImageElement;
            e.preventDefault(); // Prevent default behavior (like following links if wrapped)
            setSelectedImage(img.src);
        }
    };

    const navigateToArbeitsgruppe = (id: number) => {
        if (onNavigateToArbeitsgruppe) {
            onNavigateToArbeitsgruppe(id);
        }
    };

    return (
        <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-2xl border-1 border-gray-200 p-6 md:p-8">
                <div className="flex flex-wrap items-center gap-4 mb-6 border-b border-gray-100 pb-6">
                    <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-bold text-sm">
                                {arbeitsgruppe.challenge}
                            </span>
                            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{arbeitsgruppe.name}</h1>
                        </div>
                        <div className="flex gap-4 text-sm text-gray-500 mt-2">
                            {arbeitsgruppe.coach && <span>Coach: <span className="font-medium text-gray-900">{arbeitsgruppe.coach}</span></span>}
                            {arbeitsgruppe.room && <span>Raum: <span className="font-medium text-gray-900">{arbeitsgruppe.room}</span></span>}
                            <span>Teilnehmer: <span className="font-medium text-gray-900">{arbeitsgruppe.currentParticipantsCount} / {arbeitsgruppe.maxParticipantsCount}</span></span>
                        </div>
                    </div>
                    {isRegistered && (
                        <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-green-50 text-green-700 border border-green-200">
                            <span className="material-symbols-outlined text-base" aria-hidden="true">person</span>
                            <span className="text-sm font-semibold">Angemeldet</span>
                        </div>
                    )}
                </div>

                <div
                    className="prose prose-sm md:prose-base lg:prose-lg max-w-none prose-headings:text-gray-800 prose-p:text-gray-600 prose-a:text-primary prose-strong:text-gray-700 prose-img:rounded-lg prose-img:cursor-zoom-in prose-img:transition-transform prose-img:hover:scale-[1.01] mb-8"
                    dangerouslySetInnerHTML={{ __html: safeContent }}
                    onClick={handleContentClick}
                />

                {selectedImage && (
                    <ImageViewer
                        src={selectedImage}
                        onClose={() => setSelectedImage(null)}
                    />
                )}

                {error && (
                    <div className="p-4 mb-6 text-sm text-red-800 rounded-lg bg-red-50" role="alert">
                        {alreadyRegisteredGroupId ? (
                            <div className="space-y-2">
                                <p className="leading-relaxed">{ALREADY_REGISTERED_MESSAGE}</p>
                                <div className="flex items-center gap-1 text-sm">
                                    <span className="font-medium">Link:</span>
                                    <button
                                        type="button"
                                        onClick={() => navigateToArbeitsgruppe(alreadyRegisteredGroupId)}
                                        className="text-primary underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary transition"
                                        disabled={!onNavigateToArbeitsgruppe}
                                    >
                                        zur anmeldeten Challenge
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <>
                                <span className="font-medium">Error:</span> {error}
                            </>
                        )}
                    </div>
                )}

                <div className="flex justify-center pt-4 border-t border-gray-100">
                    {isRegistered ? (
                        <button
                            onClick={handleToggleRegistration}
                            disabled={loading}
                            className="focus:outline-none flex justify-center items-center text-white bg-red-700 hover:bg-red-800 focus:ring-4 focus:ring-red-300 font-medium rounded-lg text-sm px-5 py-2.5 mr-2 mb-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? <LoadingSpinner size="sm" variant="white" /> : 'Abmelden'}
                        </button>
                    ) : (
                        <div className="flex flex-col items-center gap-2">
                            <button
                                onClick={handleToggleRegistration}
                                disabled={loading || isFull}
                                className={`flex justify-center items-center text-white font-medium rounded-lg text-sm px-8 py-3 focus:ring-4 focus:ring-blue-300 transition-colors ${loading || isFull ? 'bg-gray-400 cursor-not-allowed' : 'bg-primary hover:bg-primary/90'}`}
                            >
                                {loading ? <LoadingSpinner size="sm" variant="white" /> : 'Hier anmelden'}
                            </button>
                            {isFull && (
                                <span className="text-sm text-amber-600 font-medium">
                                    Die Arbeitsgruppe ist leider voll.
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ArbeitsgruppeDetailPage;
